/**
 * Helper condiviso per il calcolo del totale escursione nel wizard.
 *
 * MODELLO CANONICO (tasks/wizard-steps-analysis.md §MODELLO PREZZI CANONICO):
 *
 *   markupableVendor  = vendorBase + serviceExtrasVendor
 *   clientMarkupable  = Σ round(unitVendor×(1+m))×qty  (base + service extras, per-unità)
 *   markupAmount      = clientMarkupable − markupableVendor           [INTATTO vs sconto]
 *   discountBase      = clientBase (+ serviceExtrasClient se apply_to_extras)   [prezzo cliente]
 *   lmDiscount        = round(discountBase × lmPct/100)               [sul prezzo mostrato]
 *   vendorNet         = max(0, markupableVendor − lmDiscount)         [vendor assorbe]
 *   taxes             = Σ extra kind='tax' (pass-through intero, no markup, no sconto)
 *   total             = markupAmount + vendorNet + taxes  (= clientMarkupable − lmDiscount + taxes)
 *   vendorOnline      = round(vendorNet × depositPct/100)
 *   customer_online   = markupAmount + vendorOnline                   [tasse default offline]
 *   customer_offline  = vendorNet − vendorOnline + taxes
 *
 * Markup calcolato sulla tariffa PIENA (prima dello sconto), sempre INTATTO.
 * Lo sconto è calcolato sul prezzo cliente markup-incluso; il vendor lo assorbe.
 *
 * Esempio A (price=54, markup=12%, 1 pax, tassa €5, LM 10%, deposit 100%):
 *   clientBase=60, markupAmount=6, discountBase=60, lmDiscount=6, vendorNet=48, taxes=5
 *   total=59, customer_online=54, customer_offline=5
 *
 * Esempio B (2 pax, tassa €5, LM 10%, deposit 100%):
 *   clientBase=120, markupAmount=12, discountBase=120, lmDiscount=12, vendorNet=96, taxes=5
 *   total=113, customer_online=108, customer_offline=5
 *
 * Usata da StepSummary (display) e ExcursionWizard (submit total_amount).
 */

import type { ExcursionAsset } from '@/components/booking/UnifiedBookingModal';
import type { ExcursionWizardState } from '@/components/booking/excursion/ExcursionWizard';
import { resolveExcursionExtras } from '@/lib/excursion-extras';
import { priceForDate } from '@/lib/excursion-seasonal-slots';
import { lastMinutePercent } from '@/lib/pricing/last-minute';

/** Applica markup su un prezzo unitario e arrotonda all'euro intero. */
function applyMarkup(vendorPrice: number, rate: number): number {
  return Math.round(vendorPrice * (1 + rate / 100));
}

interface ResolvedTier {
  vendorPrice: number;
  label: string;
  persons: number;
  exclusive: boolean;
}

export interface ExcursionPricingResult {
  /** Totale cliente finale (= markupAmount + vendorNet + taxes). */
  total: number;
  /**
   * Totale senza sconto per il prezzo barrato nel display.
   * = clientMarkupable + taxes  (markup pieno, no sconto)
   */
  totalBeforeLM: number;
  /** Percentuale sconto LM attiva (0 = nessun sconto). */
  lmPct: number;
  /** Sconto in € applicato al vendor (= round(discountBase × lmPct/100)). */
  lmDiscount: number;
  /** Markup piattaforma (= clientMarkupable − markupableVendor, INTATTO vs sconto). */
  markupAmount: number;
  /**
   * Totale cliente per la sola base (senza extra, no sconto, no tasse).
   * Per_person: unitClientPrice × people. Altri: round(flat × (1+m)).
   */
  baseClientTotal: number;
  /**
   * Prezzo cliente per singola persona (solo per_person; 0 altrimenti).
   * = round(slotPrice × (1+m))
   */
  unitClientPrice: number;
  /** Tasse pass-through (aggiunte intere al totale, offline by default). */
  taxes: number;
  /** Importo addebitato online = markupAmount + vendorOnline (tasse offline). */
  customerOnline: number;
  /** Saldo al porto = vendorOffline + taxes. */
  customerOffline: number;
  /** True se c'è un saldo a bordo (vendorOffline > 0 oppure taxes > 0). */
  hasSplit: boolean;
  /** Payload per il campo pricing_tier di /api/excursion-bookings (null se non tiered). */
  pricingTierPayload: {
    persons: number;
    price: number;
    label: string | null;
    exclusive: boolean;
  } | null;
}

/**
 * Calcola il prezzo finale cliente secondo il modello canonico.
 * Mirror esatto di buildExcursionEngineInput (backend) — mantenerli allineati.
 */
export function computeExcursionPricing(
  asset: ExcursionAsset,
  state: ExcursionWizardState,
): ExcursionPricingResult {
  const markupRate = asset.markupRate ?? 0;
  const depositPct = asset.depositPercent ?? 100;
  const people     = state.participants;

  /* ── Resolve tier ────────────────────────────────────────────────────────── */
  let selectedTier: ResolvedTier | null = null;
  if (state.tierKey) {
    if (state.tierKey === 'exclusive') {
      if (asset.exclusivePrice) {
        selectedTier = {
          vendorPrice: Number(asset.exclusivePrice),
          label: asset.exclusiveLabel ?? 'Esclusiva — tutta la barca',
          persons: asset.exclusiveMaxPersons ?? asset.maxCapacity ?? 1,
          exclusive: true,
        };
      }
    } else {
      const match = (asset.pricingTiers ?? []).find(
        (t) => (t.label ?? String(t.persons)) === state.tierKey,
      );
      if (match) {
        selectedTier = {
          vendorPrice: Number(match.price),
          label: match.label ?? `${match.persons} persone`,
          persons: Number(match.persons),
          exclusive: false,
        };
      }
    }
  }

  /* ── Slot price ──────────────────────────────────────────────────────────── */
  const slot = (asset.slots ?? []).find((s) => s.id === state.slotId) ?? null;
  let slotPrice: number;
  if (slot?.price_override != null) {
    slotPrice = slot.price_override;
  } else {
    const md       = state.date ? state.date.slice(5, 10) : '';
    const seasonal = asset.seasonalPeriods && md
      ? priceForDate(asset.seasonalPeriods, md)
      : null;
    slotPrice = seasonal ?? asset.price;
  }

  /* ── Vendor base ─────────────────────────────────────────────────────────── */
  const vendorBase: number = selectedTier
    ? selectedTier.vendorPrice
    : asset.pricingMode === 'per_person'
      ? slotPrice * people
      : slotPrice;

  /* ── Base cliente (per-unità) ────────────────────────────────────────────── */
  let baseClientTotal: number;
  let unitClientPrice = 0;
  if (selectedTier) {
    // Tiered / exclusive: flat → round sul flat
    baseClientTotal = applyMarkup(selectedTier.vendorPrice, markupRate);
  } else if (asset.pricingMode === 'per_person') {
    // Per persona: round(unitVendor × (1+m)) × people
    unitClientPrice = applyMarkup(slotPrice, markupRate);
    baseClientTotal = unitClientPrice * people;
  } else {
    // Per gruppo: round(flat × (1+m))
    baseClientTotal = applyMarkup(slotPrice, markupRate);
  }

  /* ── Extra: separa service (markupabili) da tax (pass-through) ───────────── */
  const extrasResolved = resolveExcursionExtras(asset.extras ?? [], state.extras, people);
  let serviceExtrasVendor = 0;
  let serviceExtrasClient = 0;
  let taxes               = 0;

  for (const line of extrasResolved.lines) {
    const lineTot = line.unit_price * line.quantity;
    if (line.kind === 'tax') {
      // Pass-through: no markup, no sconto, aggiunto intero al totale
      taxes += lineTot;
    } else {
      // Service: markup per-unità
      const unitClient = applyMarkup(line.unit_price, markupRate);
      serviceExtrasVendor += lineTot;
      serviceExtrasClient += unitClient * line.quantity;
    }
  }

  /* ── Markupable vendor e cliente ─────────────────────────────────────────── */
  const markupableVendor = vendorBase + serviceExtrasVendor;
  const clientMarkupable = baseClientTotal + serviceExtrasClient;
  const markupAmount     = clientMarkupable - markupableVendor; // INTATTO vs sconto

  /* ── Last-minute: sconto sul PREZZO CLIENTE markup-incluso, vendor assorbe ──
   * discountBase = prezzo cliente (senza tasse).
   *   apply_to_extras=false (default LM): solo baseClientTotal (base markup-inclusa).
   *   apply_to_extras=true:              baseClientTotal + serviceExtrasClient.
   * lmDiscount = round(discountBase × lmPct/100)           → euro intero
   * Vendor assorbe: vendorNet = max(0, markupableVendor − lmDiscount)
   * Markup piattaforma INTATTO: markupAmount = clientMarkupable − markupableVendor
   * Identità: total = clientMarkupable − lmDiscount + taxes
   * TODO: leggere apply_to_extras da asset.lastMinuteApplyToExtras quando disponibile.
   */
  const applyToExtras = false;
  const lmPct        = lastMinutePercent(state.date, asset.lastMinute ?? {});
  const discountBase = baseClientTotal + (applyToExtras ? serviceExtrasClient : 0);
  const lmDiscount   = lmPct > 0 ? Math.round(discountBase * lmPct / 100) : 0;
  const vendorNet    = Math.max(0, markupableVendor - lmDiscount);

  /* ── Totale e prezzo barrato ─────────────────────────────────────────────── */
  const total       = Math.round((markupAmount + vendorNet + taxes) * 100) / 100;
  // totalBeforeLM = price senza sconto per display barrato (clientMarkupable + tasse)
  const totalBeforeLM = clientMarkupable + taxes;

  /* ── Split acconto / saldo ───────────────────────────────────────────────── */
  // Deposit split su vendorNet (post-sconto, senza tasse).
  // Tasse default offline (saldo al porto).
  const vendorOnline    = Math.round(vendorNet * (depositPct / 100));
  const vendorOffline   = vendorNet - vendorOnline;
  const customerOnline  = Math.round((markupAmount + vendorOnline) * 100) / 100;
  const customerOffline = Math.round((vendorOffline + taxes) * 100) / 100;
  // Split se c'è saldo vendor offline OPPURE tasse (sempre offline)
  const hasSplit = customerOffline > 0;

  /* ── Pricing tier payload per API ────────────────────────────────────────── */
  const pricingTierPayload = selectedTier
    ? {
        persons:   selectedTier.persons,
        price:     selectedTier.vendorPrice,
        label:     selectedTier.label || null,
        exclusive: selectedTier.exclusive,
      }
    : null;

  return {
    total,
    totalBeforeLM,
    lmPct,
    lmDiscount,
    markupAmount,
    baseClientTotal,
    unitClientPrice,
    taxes,
    customerOnline,
    customerOffline,
    hasSplit,
    pricingTierPayload,
  };
}
